/**
 * Contextual Spotlight actions for entity search hits.
 * Create dialogs open in-place via CreateDialogHost; path/state kept for recent + fallback.
 */

import {
  appointmentCreateLocationState,
  buildAppointmentsUrl,
} from '@/modules/appointment/utils/appointmentViewParams';
import { buildTasksUrl } from '@/modules/task/utils/taskViewParams';

/** Section id from Global Search providers (e.g. patients, leads). */
export type SpotlightEntityType = string;

export type SpotlightContextualHit = {
  id: string;
  sectionId: SpotlightEntityType;
  primary: string;
  path: string;
};

export type SpotlightContextualBuildHelpers = {
  /** yyyy-MM-dd in organization timezone. */
  todayYmd?: string;
};

export type SpotlightContextualLabelKey =
  | 'spotlightCtxCreateAppointment'
  | 'spotlightCtxCreateAppointmentToday'
  | 'spotlightCtxCreateTask';

export type SpotlightContextualActionDef = {
  id: string;
  entityTypes: SpotlightEntityType[];
  /** i18n key under common — label is prefix; hit name appended in UI. */
  labelKey: SpotlightContextualLabelKey;
  permission?: string;
  buildPath: (
    hit: SpotlightContextualHit,
    helpers?: SpotlightContextualBuildHelpers,
  ) => string;
  /** History state for dialogs that should not appear in the URL. */
  buildState?: (
    hit: SpotlightContextualHit,
    helpers?: SpotlightContextualBuildHelpers,
  ) => object;
};

export const SPOTLIGHT_CONTEXTUAL_ACTIONS: SpotlightContextualActionDef[] = [
  {
    id: 'ctx-create-appointment',
    entityTypes: ['patients', 'leads'],
    labelKey: 'spotlightCtxCreateAppointment',
    permission: 'appointments:create',
    buildPath: () => buildAppointmentsUrl(),
    buildState: (hit) =>
      appointmentCreateLocationState(
        hit.sectionId === 'patients' ? { patientId: hit.id } : { leadId: hit.id }
      ),
  },
  {
    id: 'ctx-create-appointment-today',
    entityTypes: ['patients', 'leads'],
    labelKey: 'spotlightCtxCreateAppointmentToday',
    permission: 'appointments:create',
    buildPath: () => buildAppointmentsUrl(),
    buildState: (hit, helpers) =>
      appointmentCreateLocationState({
        ...(hit.sectionId === 'patients'
          ? { patientId: hit.id }
          : { leadId: hit.id }),
        ...(helpers?.todayYmd ? { date: helpers.todayYmd } : {}),
      }),
  },
  {
    id: 'ctx-create-task',
    entityTypes: ['patients', 'leads'],
    labelKey: 'spotlightCtxCreateTask',
    permission: 'task:create',
    buildPath: (hit) =>
      buildTasksUrl({
        view: 'list',
        mine: true,
        create: true,
        ...(hit.sectionId === 'patients'
          ? { patientId: hit.id }
          : { leadId: hit.id }),
      }),
    buildState: (hit) => ({
      taskCreate:
        hit.sectionId === 'patients'
          ? { patientId: hit.id }
          : { leadId: hit.id },
    }),
  },
];

export const getContextualActionsForHit = (
  hit: SpotlightContextualHit,
  hasPermission: (permission: string) => boolean,
): SpotlightContextualActionDef[] =>
  SPOTLIGHT_CONTEXTUAL_ACTIONS.filter((action) => {
    if (!action.entityTypes.includes(hit.sectionId)) return false;
    if (!action.permission) return true;
    return hasPermission(action.permission);
  });
